import React, { FC, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import * as ReportsQuery from 'graphql/reports.graphql';
import DropDown from '~/components/Menu/DropDown';
import DropDownItem from '~/components/Menu/DropDownItem';
import ICONS from 'components/Icons';
import IconButton from 'components/Button/IconButton';
import Modal from './Modal';
import ModalComponent from 'components/Modal';
import View from './View';
import {
  DEFAULT_VIEW,
  DeleteViewReportType,
  NEW_VIEW,
  SaveViewReportType,
  ViewType,
} from '~/pages/Reports/Submissions/types';
import './style.scss';

interface ViewsProps {
  data: ViewType[];
  activeViewId: string;
  onViewSelected: (view: ViewType) => void;
  refetchViews?: () => void;
}

const MAX_TABS = 3;

const Views: FC<ViewsProps> = ({ data, activeViewId, onViewSelected, refetchViews }) => {
  const [editedView, setEditedView] = useState<Partial<ViewType>>();
  const [toBeDeletedView, setToBeDeletedView] = useState<Partial<ViewType>>();

  const [selectedHiddenView, setSelectedHiddenView] = useState<ViewType | null>(null);
  const visibleViewsCount = MAX_TABS - 1;
  // the last visible element can be the last hidden view the user clicked
  const lastVisibleView = selectedHiddenView ? selectedHiddenView : data[visibleViewsCount];
  const visibleViews = [...data.slice(0, visibleViewsCount), ...(lastVisibleView ? [lastVisibleView] : [])];
  const hiddenViews = data.slice(visibleViewsCount).filter(view => view.id !== lastVisibleView.id);

  useEffect(() => {
    const selectedViewIndex = data.findIndex(view => view.id === activeViewId);
    if (selectedViewIndex > visibleViewsCount) setSelectedHiddenView(data[selectedViewIndex]);
  }, [data, activeViewId, visibleViewsCount, setSelectedHiddenView]);

  const [saveView, { loading: loadingSaveView }] = useMutation<SaveViewReportType>(ReportsQuery.SaveReportView, {
    onCompleted: () => refetchViews && refetchViews(),
  });

  const [deleteView, { loading: loadingDeleteView }] = useMutation<DeleteViewReportType>(
    ReportsQuery.DeleteReportView,
    {
      onCompleted: () => refetchViews && refetchViews(),
    },
  );

  const handleOnSaveView = async (viewName: string) => {
    const input = {
      ...(editedView?.id ? { id: editedView.id } : {}),
      name: viewName || 'Saved view',
      configuration: JSON.stringify(editedView?.configuration),
    };

    await saveView({
      variables: { input: input },
    }).then(result => {
      const savedView = result.data?.saveReportView;
      if (savedView) {
        onViewSelected(savedView);
        if (!editedView?.id && data.length > visibleViewsCount) setSelectedHiddenView(savedView);
      }
      setEditedView(undefined);
    });
  };

  const handleOnDeleteView = async () => {
    await deleteView({ variables: { id: toBeDeletedView?.id } }).then(() => {
      if (toBeDeletedView?.id == activeViewId) {
        onViewSelected(data[0]);
      }
      setSelectedHiddenView(null);
      setToBeDeletedView(undefined);
    });
  };

  const ref = useRef<HTMLElement>(null);

  return (
    <div className="views">
      <div className="desktop-only">{ICONS['reports']}</div>
      <div className="views-items">
        {data.length === 0 && (
          <View
            key={DEFAULT_VIEW.id}
            label={DEFAULT_VIEW.name}
            viewKey={DEFAULT_VIEW.id}
            viewActive={activeViewId}
            handleViewRename={() => setEditedView(NEW_VIEW)}
          />
        )}
        {visibleViews.map(view => (
          <View
            key={view.id}
            label={view.name}
            viewKey={view.id}
            onClick={() => onViewSelected(view)}
            viewActive={activeViewId}
            handleViewRename={() => setEditedView(view)}
            handleViewDelete={() => setToBeDeletedView(view)}
          />
        ))}
        {hiddenViews.length > 0 && (
          <div className="view-button">
            <DropDown
              outsideRef={ref}
              openedIcon="menu"
              closedIcon="menu_open"
              iconSide="RIGHT"
              buttonChildren={<span className="view-name">{hiddenViews.length} more...</span>}
              className="more-views">
              {hiddenViews.map(view => (
                <DropDownItem
                  icon="reports"
                  key={view.id}
                  onClick={() => {
                    setSelectedHiddenView(view);
                    onViewSelected(view);
                  }}>
                  {view.name}
                </DropDownItem>
              ))}
            </DropDown>
          </div>
        )}
      </div>
      <IconButton icon="plus_new" onClick={() => setEditedView(NEW_VIEW)} />
      {editedView && (
        <Modal
          actionLabel={editedView?.id ? 'Rename view' : 'New view'}
          initialViewName={editedView?.id ? (editedView?.name as string) : ''}
          handleOnSaveView={async (viewName: string) => await handleOnSaveView(viewName)}
          loadingSaveView={loadingSaveView}
          showModal={!!editedView}
          setShowModal={setEditedView}
        />
      )}
      {toBeDeletedView && (
        <ModalComponent
          header="Delete view"
          headerIcon="warning"
          handleOnCancel={() => setToBeDeletedView(undefined)}
          handleOnConfirm={() => void handleOnDeleteView()}
          confirmlabel={'Confirm'}
          denyLabel="Not now"
          visible={true}
          className="confirm-delete-view-modal"
          confirmButtonProps={{ disabled: loadingDeleteView }}>
          <span>
            Are you sure you want to delete &quot;{toBeDeletedView.name}&quot; view?{' '}
            <strong>This change can&apos;t be undone.</strong>
          </span>
        </ModalComponent>
      )}
    </div>
  );
};

export default Views;
